/**
 * Camada React do modo motor: os mesmos hooks que o widget usa do
 * `@callixbrasil/client-sdk-react`, só que alimentados pelo `EngineClient`.
 * A tela não sabe se fala com o SDK no navegador ou com o motor no servidor.
 *
 * O que o SDK devolve como objetos ricos (Call, CallInfo, CallOperator) aqui vira
 * objetos mínimos com os campos e métodos que as telas realmente usam.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from 'react';
import type { Call, CallData, CallInfo, CallOperator, CallOperatorPublicEvents, CallQualification, ServiceBreak, UserSessionDropOriginator } from '@callixbrasil/client-sdk';
import type { CallOperatorState } from '@callixbrasil/client-sdk-react';
import type { AgentView, CallSnapshot, Command, Qualification } from '../../engine/protocol';
import { EngineClient, EngineSignal, type EngineClientState, type EngineTranscriptState } from './engineClient';

export const EngineClientContext = createContext<EngineClient | null>(null);

export function EngineClientProvider({ client, children }: { client: EngineClient; children: ReactNode }) {
  return <EngineClientContext.Provider value={client}>{children}</EngineClientContext.Provider>;
}

/** o cliente do motor quando o widget está no modo motor; null no modo SDK */
export function useEngineClient(): EngineClient | null {
  return useContext(EngineClientContext);
}

export function useEngineState(client: EngineClient): EngineClientState {
  return useSyncExternalStore(client.subscribe.bind(client), client.getState.bind(client), client.getState.bind(client));
}

// ------------------------------------------------------------------ mapeamentos (puros, testáveis)

/** pausa de reserva: só quando a vista do motor ainda não traz as pausas do serviço (motor anterior ao 2.8.1); o motor então usa ENGINE_BREAK_ID ou a primeira */
export const ENGINE_BREAKS: ServiceBreak[] = [{ id: 0, name: 'Pausa' }];

/** pausas do serviço vindas da vista do motor (as mesmas que o SDK lista); sem elas, a de reserva */
export function breaksFromView(view: AgentView | null): ServiceBreak[] {
  const breaks = view?.snapshot?.leg.breaks;
  return breaks && breaks.length > 0 ? breaks.map((b) => ({ id: b.id, name: b.name })) : ENGINE_BREAKS;
}

/** pausa em vigor: a que o motor informa; sem ela, a primeira da lista */
function currentBreakFromView(view: AgentView): ServiceBreak {
  return view.snapshot?.leg.break ?? breaksFromView(view)[0];
}

/** comando de pausa com a pausa escolhida pela tela (o motor cai na padrão se não a conhecer) */
export function pauseCommand(breakId?: number): Command {
  return typeof breakId === 'number' ? { name: 'pause', breakId } : { name: 'pause' };
}

export function toCallQualification(q: Qualification): CallQualification {
  return { id: q.id, name: q.name, isSystemQualification: false, systemQualificationType: 0 } as unknown as CallQualification;
}

export function qualificationsFromView(view: AgentView | null, kind: Qualification['kind']): CallQualification[] {
  return (view?.snapshot?.leg.qualifications ?? []).filter((q) => q.kind === kind).map(toCallQualification);
}

/** a chamada como a tela a conhece, com os métodos que ela chama */
export class EngineCall {
  private readonly listeners = new Map<string, Set<(...args: unknown[]) => void>>();
  snapshot: CallSnapshot;

  constructor(
    private readonly client: EngineClient,
    snapshot: CallSnapshot
  ) {
    this.snapshot = snapshot;
  }

  get id(): string {
    return this.snapshot.id;
  }

  get direction(): 'incoming' | 'outgoing' {
    return this.snapshot.direction;
  }

  get data(): { destinationNumber?: string; campaignContactPhoneId?: number; answeredAt?: Date } {
    return {
      destinationNumber: this.snapshot.contact?.phone,
      campaignContactPhoneId: this.snapshot.campaignContactPhoneId,
      answeredAt: this.snapshot.answeredAt ? new Date(this.snapshot.answeredAt) : undefined,
    };
  }

  get callerNumber(): string | undefined {
    return this.snapshot.contact?.phone;
  }

  get peerDisplayName(): string | undefined {
    return this.snapshot.contact?.name;
  }

  get isEstablished(): boolean {
    return this.snapshot.state === 'active';
  }

  get isEnded(): boolean {
    return this.snapshot.state === 'ended';
  }

  /** segundos desde o atendimento (ou desde o início, enquanto toca) */
  get currentDurationSeconds(): number {
    const from = this.snapshot.answeredAt ?? this.snapshot.startedAt;
    return Math.max(0, Math.floor((Date.now() - from) / 1000));
  }

  answer(): void {
    this.client.command({ name: 'answer' });
  }

  reject(): void {
    this.client.command({ name: 'reject' });
  }

  hangup(): void {
    this.client.command({ name: 'hangup' });
  }

  isOnHold(): { local: boolean; remote: boolean } {
    return { local: false, remote: false };
  }

  async hold(): Promise<void> {}

  async unhold(): Promise<void> {}

  isMuted(): boolean {
    return this.client.getState().inputMuted;
  }

  mute(): void {
    this.client.setInputMuted(true);
  }

  unmute(): void {
    this.client.setInputMuted(false);
  }

  sendDtmf(tones: string): void {
    this.client.command({ name: 'dtmf', tones });
  }

  on(event: string, listener: (...args: unknown[]) => void): this {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener);
    return this;
  }

  once(event: string, listener: (...args: unknown[]) => void): this {
    const wrapped = (...args: unknown[]) => {
      this.off(event, wrapped);
      listener(...args);
    };
    return this.on(event, wrapped);
  }

  off(event: string, listener: (...args: unknown[]) => void): this {
    this.listeners.get(event)?.delete(listener);
    return this;
  }

  toCallData(): CallData {
    return {
      id: this.snapshot.id,
      direction: this.snapshot.direction,
      destinationNumber: this.snapshot.contact?.phone ?? '',
      answeredAt: this.snapshot.answeredAt ? new Date(this.snapshot.answeredAt).toISOString() : undefined,
      campaignContactPhoneId: this.snapshot.campaignContactPhoneId,
      isMuted: this.snapshot.muted,
    } as unknown as CallData;
  }
}

export function operatorStateFromView(view: AgentView | null, call: EngineCall | null): CallOperatorState | undefined {
  if (!view) return undefined;
  if (view.sessionLost?.originator === 'anotherDevice') return { state: 'startupError', error: 'userIsAlreadyConnectedOnAnotherDevice' };
  const leg = view.snapshot?.leg;
  if (!leg) {
    if (view.engine === 'starting') return { state: 'starting' };
    if (view.engine === 'running') return { state: 'starting' };
    return { state: 'offline' };
  }
  const asCall = (call ?? undefined) as unknown as Call;
  switch (leg.status) {
    case 'starting':
      return { state: 'starting' };
    case 'available':
      return { state: 'idle' };
    case 'ringing':
      return { state: 'callRinging', call: asCall };
    case 'dialing':
      return leg.call?.state === 'ringing' ? { state: 'manualCallRinging', call: asCall } : { state: 'manualCallSetup', call: asCall };
    case 'inCall':
      return { state: 'callInProgress', call: asCall };
    case 'afterCall':
      return { state: 'afterCall' };
    case 'onBreak':
      return { state: 'onBreak', break: currentBreakFromView(view) };
    case 'offline':
      return { state: 'offline' };
    case 'error':
      return { state: 'startupError', error: 'unknownError' };
  }
}

export function callInfoFromView(view: AgentView | null): CallInfo | undefined {
  const leg = view?.snapshot?.leg;
  const call = leg?.call;
  if (!leg || !call) return undefined;
  const successQualifications = qualificationsFromView(view, 'success');
  const discardQualifications = qualificationsFromView(view, 'discard');
  const contact = call.contact;
  if (contact?.campaign) {
    const info = {
      campaign: {
        id: 0,
        name: contact.campaign,
        type: 0,
        requireCallComments: false,
        enableTransfer: false,
        enableScheduledCall: false,
        successQualifications,
        discardQualifications,
        previewDuration: '',
        showCampaignModelOnService: false,
        skipCallQualification: false,
      },
      campaignList: { id: 0, name: '' },
      campaignContact: { id: call.campaignContactPhoneId ?? 0, label: contact.name, phoneNumber: contact.phone },
    };
    return { type: 'campaign', info } as unknown as CallInfo;
  }
  return { type: 'manual', info: { successQualifications, discardQualifications } };
}

// ------------------------------------------------------------------ hooks (recebem o cliente)

/** mantém a mesma instância de EngineCall enquanto a chamada for a mesma */
function useEngineCall(client: EngineClient): EngineCall | null {
  const state = useEngineState(client);
  const snapshot = state.view?.snapshot?.leg.call ?? null;
  const ref = useRef<EngineCall | null>(null);
  if (!snapshot || snapshot.state === 'ended') {
    ref.current = null;
  } else if (!ref.current || ref.current.id !== snapshot.id) {
    ref.current = new EngineCall(client, snapshot);
  } else {
    ref.current.snapshot = snapshot;
  }
  return ref.current;
}

export function useEngineOperatorState(client: EngineClient): CallOperatorState | undefined {
  const state = useEngineState(client);
  const call = useEngineCall(client);
  return useMemo(() => operatorStateFromView(state.view, call), [state.view, call]);
}

export function useEngineCurrentCall(client: EngineClient): Call | undefined {
  const call = useEngineCall(client);
  return (call ?? undefined) as unknown as Call | undefined;
}

export function useEngineCurrentCallInfo(client: EngineClient): CallInfo | undefined {
  const state = useEngineState(client);
  const view = state.view;
  const key = JSON.stringify([view?.snapshot?.leg.call?.id, view?.snapshot?.leg.call?.contact, view?.snapshot?.leg.qualifications]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => callInfoFromView(view), [key]);
}

export function useEngineQualifications(client: EngineClient, kind: Qualification['kind']): CallQualification[] {
  const state = useEngineState(client);
  const raw = state.view?.snapshot?.leg.qualifications ?? [];
  const key = JSON.stringify(raw);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => qualificationsFromView(state.view, kind), [key, kind]);
}

export interface EngineOperatorControls {
  becomeAvailable(): void;
  goOffline(): void;
  enterOnBreak(breakId: number): void;
  makeManualCall(phoneNumber: string): void;
  finishAfterCall(data: { result: 'success' | 'failure'; qualificationId?: number }): void;
}

export function useEngineControls(client: EngineClient): EngineOperatorControls {
  return useMemo(
    () => ({
      becomeAvailable: () => {
        const status = client.getState().view?.snapshot?.leg.status;
        if (status === 'onBreak') client.command({ name: 'resume' });
      },
      goOffline: () => undefined,
      enterOnBreak: (breakId) => client.command(pauseCommand(breakId)),
      makeManualCall: (phoneNumber: string) => client.command({ name: 'dial', phone: phoneNumber }),
      finishAfterCall: (data) => {
        if (typeof data.qualificationId === 'number') client.command({ name: 'qualify', qualificationId: data.qualificationId, result: data.result === 'failure' ? 'discard' : 'success' });
        else client.command({ name: 'finish' });
      },
    }),
    [client]
  );
}

export interface EngineCallControls {
  answer(): void;
  reject(): void;
  hangup(): void;
  isOnHold(): { local: boolean; remote: boolean };
  hold(): Promise<void>;
  unhold(): Promise<void>;
  isMuted(): boolean;
  mute(): void;
  unmute(): void;
}

export function useEngineCurrentCallControls(client: EngineClient): EngineCallControls {
  return useMemo(
    () => ({
      answer: () => client.command({ name: 'answer' }),
      reject: () => client.command({ name: 'reject' }),
      hangup: () => client.command({ name: 'hangup' }),
      isOnHold: () => ({ local: false, remote: false }),
      hold: async () => undefined,
      unhold: async () => undefined,
      isMuted: () => client.getState().inputMuted,
      mute: () => client.setInputMuted(true),
      unmute: () => client.setInputMuted(false),
    }),
    [client]
  );
}

export function useEngineAudioInputMuted(client: EngineClient): [boolean, (muted: boolean) => void] {
  const state = useEngineState(client);
  const set = useCallback((muted: boolean) => client.setInputMuted(muted), [client]);
  return [state.inputMuted, set];
}

export function useEngineAudioOutputMuted(client: EngineClient): [boolean, (muted: boolean) => void] {
  const state = useEngineState(client);
  const set = useCallback((muted: boolean) => client.setOutputMuted(muted), [client]);
  return [state.outputMuted, set];
}

export function useEngineAudioOutputVolume(client: EngineClient): [number, (volume: number) => void] {
  const state = useEngineState(client);
  const set = useCallback((volume: number) => client.setOutputVolume(volume), [client]);
  return [state.outputVolume, set];
}

export function useEngineEventListener<E extends keyof CallOperatorPublicEvents>(client: EngineClient, event: E, listener: CallOperatorPublicEvents[E]): void {
  const ref = useRef(listener);
  ref.current = listener;
  useEffect(() => {
    if (event !== 'userSessionDropped') return;
    return client.onSessionLost((lost) => {
      (ref.current as CallOperatorPublicEvents['userSessionDropped'])({ originator: lost.originator as UserSessionDropOriginator });
    });
  }, [client, event]);
}

/** o "operador" que as telas recebem de useCallOperator(): sinais e os poucos métodos usados */
export interface EngineOperator {
  breaks: EngineSignal<ServiceBreak[]>;
  user: EngineClient['userSignal'];
  audioOutput: { setAudioOutputDevice(id: string): void; setVolume(volume: number): void; setMute(muted: boolean): void };
  stop(): void;
  goOffline(): void;
  becomeAvailable(): void;
  enterOnBreak(breakId: number): void;
  makeManualCall(phoneNumber: string): void;
  finishAfterCall(data: { result: 'success' | 'failure'; qualificationId?: number }): void;
}

const operators = new WeakMap<EngineClient, EngineOperator>();

export function engineOperator(client: EngineClient): EngineOperator {
  let operator = operators.get(client);
  if (!operator) {
    const breaks = new EngineSignal<ServiceBreak[]>(breaksFromView(client.getState().view));
    client.subscribe(() => breaks.set(breaksFromView(client.getState().view)));
    operator = {
      breaks,
      user: client.userSignal,
      audioOutput: {
        setAudioOutputDevice: (id) => void client.setOutputDevice(id).catch(() => undefined),
        setVolume: (volume) => client.setOutputVolume(volume),
        setMute: (muted) => client.setOutputMuted(muted),
      },
      stop: () => undefined,
      goOffline: () => undefined,
      becomeAvailable: () => {
        if (client.getState().view?.snapshot?.leg.status === 'onBreak') client.command({ name: 'resume' });
      },
      enterOnBreak: (breakId) => client.command(pauseCommand(breakId)),
      makeManualCall: (phoneNumber) => client.command({ name: 'dial', phone: phoneNumber }),
      finishAfterCall: (data) => {
        if (typeof data.qualificationId === 'number') client.command({ name: 'qualify', qualificationId: data.qualificationId, result: data.result === 'failure' ? 'discard' : 'success' });
        else client.command({ name: 'finish' });
      },
    };
    operators.set(client, operator);
  }
  return operator;
}

export function useEngineOperator(client: EngineClient): CallOperator {
  return useMemo(() => engineOperator(client) as unknown as CallOperator, [client]);
}

export function useEngineTranscript(client: EngineClient): EngineTranscriptState {
  return useEngineState(client).transcript;
}
